/**
 * The hub's 3D model, as every surface reads it (`t_hub_bim_model`, 2026-10-27).
 *
 * There is ONE model per hub, so there is ONE fetch and ONE poll for the whole
 * app. The 3D page and any embedded view share this store; whoever is mounted
 * retains the poll, and the last one to leave stops it.
 *
 * What the store does NOT do is load bytes. The scene owns that
 * (`useBimScene.ensureModel`), because the canvas, the camera and the parsed
 * meshes are the scene cache's — not a store's. The split is:
 *
 *   · the store answers "which revision does the hub serve?" and raises
 *     `revision` when the revision on screen is not the served one;
 *   · a mounted surface watches `revision`, asks `useBimScene.ensureModel` for
 *     the served URL (camera-preserving), and reports back with `noteLoaded`.
 *
 * `setPauseProvider` is the page's half: a swap must not run while an edit is in
 * flight or while the operator is looking at another tab, and only the page knows
 * that. The store asks the provider at tick time.
 */
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { fetchHubBimModels, type BimModelRow } from '@/services/bimModelApi';
import {
  BIM_MODEL_POLL_MS,
  bimModelFileUrl,
  bimModelSignature,
  newestBimModelRow,
  shouldSwapBimModel,
} from '@/utils/bimModelRefresh';

export const useBimModelStore = defineStore('bimModel', () => {
  /** Every row the hub answered with (0 or 1 today). */
  const rows = ref<BimModelRow[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  /**
   * Has the hub ANSWERED at least once? "No model" is only a fact after an
   * answer, so a surface that decides whether to draw the model at all reads
   * this beside `hasModel` — before it, the surface stays as it always was.
   */
  const answered = ref(false);

  /** The revision the SERVED row names — `null` when the hub has no model. */
  const servedSignature = ref<string | null>(null);
  /** The revision a mounted surface has on screen — `null` until one loads. */
  const loadedSignature = ref<string | null>(null);
  /** Bumped when a mounted surface should exchange its model for the served one. */
  const revision = ref(0);
  /** The last pause verdict (the page's provider decides it). */
  const paused = ref(false);

  const servedRow = computed<BimModelRow | null>(() => newestBimModelRow(rows.value));
  const servedUrl = computed<string | null>(() => {
    const row = servedRow.value;
    return row === null ? null : bimModelFileUrl(row);
  });
  const hasModel = computed(() => servedRow.value !== null);

  let pauseProvider: (() => boolean) | null = null;
  let pollers = 0;
  let timer: ReturnType<typeof setInterval> | null = null;
  let ensureInFlight: Promise<void> | null = null;

  /** Read the rows the hub serves. A refusal is reported, never thrown to a tick. */
  async function refresh(): Promise<void> {
    loading.value = true;
    try {
      const next = await fetchHubBimModels();
      rows.value = next;
      servedSignature.value = bimModelSignature(newestBimModelRow(next));
      error.value = null;
      answered.value = true;
    } catch (caught) {
      error.value = (caught as Error).message ?? 'The 3D model could not be read';
      throw caught;
    } finally {
      loading.value = false;
    }
  }

  /** Read once if nothing is known yet (a surface mounting on a cold app). */
  async function ensureLoaded(): Promise<void> {
    if (rows.value.length > 0) return;
    // The dashboard and its model card mount together and both ask; one read
    // answers both (and a second surface never opens a duplicate request).
    if (!ensureInFlight) {
      ensureInFlight = refresh().finally(() => {
        ensureInFlight = null;
      });
    }
    await ensureInFlight;
  }

  /**
   * The revision a surface just put on screen. `null` clears it (the scene was
   * dropped), so the next tick sees "nothing loaded" and waits for a load.
   */
  function noteLoaded(signature: string | null): void {
    loadedSignature.value = signature;
  }

  /** Should a mounted surface exchange what it has for the served revision? */
  function needsSwap(): boolean {
    return shouldSwapBimModel({
      loaded: loadedSignature.value,
      served: servedSignature.value,
      paused: paused.value,
    });
  }

  /** The page's own "may I swap right now?" rule. */
  function setPauseProvider(provider: (() => boolean) | null): void {
    pauseProvider = provider;
  }

  async function tick(): Promise<void> {
    paused.value = pauseProvider ? pauseProvider() : false;
    if (paused.value) return;
    try {
      await refresh();
    } catch {
      // The hub may be restarting, or between the delete and the create of a
      // re-serve. The next tick retries; a failed poll is not a page fault.
      return;
    }
    if (needsSwap()) revision.value += 1;
  }

  /** Run a tick now (the page came back into view) instead of waiting. */
  function pokeNow(): void {
    void tick();
  }

  function startTimer(): void {
    if (timer !== null) return;
    timer = setInterval(() => {
      void tick();
    }, BIM_MODEL_POLL_MS);
  }

  function stopTimer(): void {
    if (timer === null) return;
    clearInterval(timer);
    timer = null;
  }

  /**
   * A surface is mounted and wants the live revision. Ref-counted: the 3D page
   * and an embed both retain, and the poll runs until the last one releases.
   */
  function retainPolling(): void {
    pollers += 1;
    if (pollers === 1) startTimer();
  }

  function releasePolling(): void {
    pollers = Math.max(0, pollers - 1);
    if (pollers === 0) {
      stopTimer();
      paused.value = true;
    }
  }

  /** Hub switch / sign-out: forget everything and stop the timer. */
  function reset(): void {
    stopTimer();
    pollers = 0;
    rows.value = [];
    servedSignature.value = null;
    loadedSignature.value = null;
    revision.value = 0;
    paused.value = false;
    error.value = null;
    answered.value = false;
    ensureInFlight = null;
    pauseProvider = null;
  }

  return {
    rows,
    loading,
    error,
    answered,
    servedRow,
    servedUrl,
    servedSignature,
    loadedSignature,
    revision,
    paused,
    hasModel,
    refresh,
    ensureLoaded,
    noteLoaded,
    needsSwap,
    setPauseProvider,
    tick,
    pokeNow,
    retainPolling,
    releasePolling,
    reset,
  };
});
